import { ManufacturerProductPropertyOptionDetail } from '@deltek/specification-client/core/Models/product'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { arrayMove } from '@dnd-kit/sortable'
import {
    Backdrop,
    CircularProgress,
    IconButton,
    makeStyles,
    Theme,
    Typography,
} from '@material-ui/core'
import { AddOutlined } from '@material-ui/icons'
import clsx from 'clsx'
import React, {
    Dispatch,
    FC,
    RefObject,
    SetStateAction,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { v4 as uuidv4 } from 'uuid'

import listingColors from 'pages/ProductsPage/ProductDetailsPage/listingColors'

import { useApolloClient, useMutation } from '@apollo/client'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import { GetIsManufacturerOptionDetailsRemovable } from 'api/graphQL/queries/product.queries'
import {
    ProductContext,
    ProductContextType,
} from 'pages/ProductsPage/ProductDetailsPage/components/ListingDetailsPage'
import { stripDoubleSpaces } from 'shared/utils/inputUtils'
import DndProperties from '../../components/DndProperties'
import { ERROR_CANNOT_REMOVE_OPTION } from '../../constants'
import { PPL_ACTIONS, usePPLContext } from '../../PPLProvider'
import { checkForDuplicates } from '../../utils/optionUtils'
import { generateNewOptionGroups, insertToIndex } from '../nonMSReducer'
import { NonMSSubPropertyType } from '../nonMSTypes'

export const AFFIX_LIMIT = 2000

const {
    propertiesDivider,
    addOptionFillButton,
    addOptionButtonSecondary,
    addOptionButtonHover,
    buttonPrimary,
    defaultLight,
    errorColor,
    boxBorder,
    productDetailsText,
} = listingColors

const useStyles = makeStyles((theme: Theme) => ({
    backdrop: {
        zIndex: theme.zIndex.drawer + 1,
        color: theme.palette.common.white,
    },
    subPropertyNameInput: {
        width: '100%',
        border: 0,
        marginLeft: theme.spacing(0.75),
        marginRight: theme.spacing(0.75),
        padding: theme.spacing(0.75),
        fontWeight: 500,
        backgroundColor: defaultLight,
        textOverflow: 'ellipsis',
        '&::placeholder': {
            color: productDetailsText,
            fontWeight: 500,
        },
        '&.error': {
            borderBottom: `1px solid ${errorColor}`,
            outline: 'none',
        },
        '&:focus': {
            outline: 'none',
            borderBottom: `1px solid ${boxBorder}`,
        },
    },
    optionGroupContainer: {
        display: 'inline-flex',
        width: 'inherit',
        height: 'auto',
        flexWrap: 'wrap',
        alignItems: 'center',
        maxWidth: '100%',
        paddingTop: theme.spacing(1),
    },
    addIcon: {
        width: pxToRem(20),
        height: pxToRem(20),
        fill: addOptionButtonSecondary,
        '&:hover': {
            fill: addOptionButtonSecondary,
        },
    },
    addPremiumPlusProperty: {
        alignSelf: 'center',
        marginBottom: theme.spacing(1),
        padding: pxToRem(5),
        gap: pxToRem(10),
        width: pxToRem(24),
        height: pxToRem(24),
        color: defaultLight,
        backgroundColor: addOptionFillButton,
        border: `1px solid ${addOptionButtonSecondary}`,
        borderRadius: pxToRem(100),
        '&:hover': {
            backgroundColor: addOptionButtonHover,
            border: `1px solid ${addOptionButtonSecondary}`,
            cursor: 'pointer',
        },
    },
    addButton: {
        fontSize: theme.spacing(1.5),
        color: buttonPrimary,
        border: 0,
        backgroundColor: defaultLight,
        '&.disabled': {
            color: propertiesDivider,
        },
    },
    subPropertyNameInputContainer: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        paddingRight: theme.spacing(1),
        paddingTop: theme.spacing(1),
    },
    errorLabel: {
        color: errorColor,
        fontSize: '10px',
        marginLeft: theme.spacing(1),
    },
    addPropertyHover: {
        width: 'unset',
    },
    prefixPostFix: {
        fontSize: pxToRem(13),
        display: 'inline',
        wordBreak: 'break-word',
        marginRight: theme.spacing(1),
        marginBottom: theme.spacing(1),
        marginLeft: theme.spacing(1),
    },
    editable: {
        minWidth: theme.spacing(1.5),
        fontSize: pxToRem(13),
        marginBottom: theme.spacing(1),
        border: 'none',
        outline: 'none',
        '&:hover': {
            cursor: 'text',
        },
        '&:focus': {
            margin: theme.spacing(0, 1, 1),
        },
    },
}))

interface SubPropertyProps {
    index: number
    subProperty: NonMSSubPropertyType
    groupIndex: number
    isAffixActive?: boolean
    setIsAffixActive?: Dispatch<SetStateAction<boolean>>
}

const NonMSEditableOptionGroup: FC<SubPropertyProps> = ({
    subProperty,
    groupIndex,
    index,
    isAffixActive,
    setIsAffixActive,
}) => {
    const classes = useStyles()

    const { nonMSProperties: state, dispatch } = usePPLContext()

    const { product } = useContext<Partial<ProductContextType>>(ProductContext)
    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)

    const client = useApolloClient()

    const editablePrefix: RefObject<HTMLSpanElement> = useRef(null)

    const isPrefixEditable = useMemo(
        () =>
            !!state.prefixEditable.length &&
            state.prefixEditable.includes(subProperty.propertyId),
        [state.prefixEditable, subProperty.propertyId]
    )

    const subProperties = useMemo(() => state.editableProperty.subProperties, [
        state.editableProperty.subProperties,
    ])

    const [isRemoveLoading, setIsRemoveLoading] = useState(false)
    const [groupPrefix, setGroupPrefix] = useState(
        subProperty.optionGroups[groupIndex].prefix ?? ''
    )
    const [groupPostfix, setGroupPostfix] = useState(
        subProperty.optionGroups[groupIndex].postfix ?? ''
    )

    const [subPropertyOptions, setSubPropertyOptions] = useState<
        Array<Partial<ManufacturerProductPropertyOptionDetail>>
    >(subProperty.optionGroups[groupIndex].options ?? [])

    const hasSubpropertyOptions = useMemo(() => subPropertyOptions.length, [
        subPropertyOptions,
    ])

    const [selectedOptions, setSelectedOptions] = useState<Array<string>>(
        subProperty.supportedOptionIds ?? []
    )

    const [isAddActive, setIsAddActive] = useState(false)

    const determineSelected = useCallback(
        (value, optionId) =>
            !!value && !!selectedOptions && selectedOptions.includes(optionId),
        [selectedOptions]
    )

    const handleDragEnd = useCallback(
        (active, over) => {
            if (!!active && !!over && active.id !== over.id) {
                setSubPropertyOptions(
                    (
                        subPropArray: Array<
                            Partial<ManufacturerProductPropertyOptionDetail>
                        >
                    ) => {
                        const oldItem = subPropArray.findIndex(
                            (
                                item: Partial<
                                    ManufacturerProductPropertyOptionDetail
                                >
                            ) => item.optionId === active.id
                        )
                        const newItem = subPropArray.findIndex(
                            (
                                item: Partial<
                                    ManufacturerProductPropertyOptionDetail
                                >
                            ) => item.optionId === over.id
                        )

                        const newArray = arrayMove(
                            subPropArray,
                            oldItem,
                            newItem
                        )

                        dispatch({
                            type: PPL_ACTIONS.setPremiumOptions,
                            payload: {
                                subPropIndex: index,
                                subPropertyOptions: newArray,
                                selectedOptions,
                                groupIndex,
                            },
                        })
                        return newArray
                    }
                )
            }
        },
        [dispatch]
    )

    const handleRemoveClick = useCallback(
        async (optionId: string) => {
            const optionGroupId = subProperty.optionGroups[groupIndex].groupId
            setIsRemoveLoading(true)

            try {
                const {
                    data: {
                        getManufacturerOptionDetails: {
                            manufacturerOptionGroupDetails,
                        },
                    },
                } = await client.query({
                    query: GetIsManufacturerOptionDetailsRemovable,
                    variables: {
                        input: {
                            listingId: product?.listingId ?? '',
                            details: [
                                {
                                    propertyId: subProperty.propertyId,
                                    optionGroupId,
                                    optionId,
                                },
                            ],
                        },
                    },
                })

                if (!!manufacturerOptionGroupDetails[0].isDetailsRemovable) {
                    const newSubProperties = subPropertyOptions.filter(
                        (
                            option: Partial<
                                ManufacturerProductPropertyOptionDetail
                            >
                        ) => option.optionId !== optionId
                    )

                    setSubPropertyOptions(newSubProperties)

                    dispatch({
                        type: PPL_ACTIONS.setPremiumOptions,
                        payload: {
                            subPropIndex: index,
                            subPropertyOptions: newSubProperties,
                            selectedOptions: selectedOptions,
                            groupIndex,
                        },
                    })
                } else {
                    throw new Error()
                }
            } catch (error) {
                showGlobalSnackbar({
                    variables: {
                        message: ERROR_CANNOT_REMOVE_OPTION,
                        variant: 'error',
                        id: 'RemoveOptionFailed',
                    },
                })
            } finally {
                setIsRemoveLoading(false)
            }
        },
        [dispatch, index, subProperties, selectedOptions]
    )

    const handleSetSelected = useCallback(
        (id: string) => {
            const newSelectedOptions = selectedOptions.includes(id)
                ? selectedOptions?.filter((d) => d !== id)
                : [...selectedOptions, id]
            setSelectedOptions(newSelectedOptions)

            const optionIndex = subPropertyOptions.findIndex(
                (option) => option.optionId === id
            )
            const option = subPropertyOptions[optionIndex]

            dispatch({
                type: PPL_ACTIONS.setPremiumOptions,
                payload: {
                    subPropIndex: index,
                    subPropertyOptions: insertToIndex(
                        subPropertyOptions,
                        optionIndex,
                        { ...option, isSupported: !option.isSupported },
                        true
                    ),
                    selectedOptions: newSelectedOptions,
                    groupIndex,
                },
            })
        },
        [dispatch, index, selectedOptions, subPropertyOptions]
    )

    const insertPremiumOption = useCallback(
        (property: string | Record<string, string>) => {
            if (property) {
                let valueFragment = {
                    value: property as string,
                    isUnitOfMeasure: false,
                    imperialUnits: '',
                    metricUnits: '',
                }

                if (typeof property !== 'string') {
                    valueFragment = {
                        value: `${property.imperialUnits} (${property.metricUnits})`,
                        isUnitOfMeasure: true,
                        imperialUnits: property.imperialUnits ?? '',
                        metricUnits: property.metricUnits ?? '',
                    }
                }

                const hasDuplicate = checkForDuplicates({
                    property,
                    premiumOptions: subPropertyOptions,
                })

                if (hasDuplicate) {
                    return true
                }

                const optionId = uuidv4()

                const newSubProp = [
                    ...subPropertyOptions,
                    {
                        isOther: false,
                        isPremiumPlus: true,
                        isPremium: true,
                        isSupported: true,
                        optionId,
                        ...valueFragment,
                    },
                ]

                const newSelectedOptions = [...selectedOptions, optionId]

                setSubPropertyOptions(newSubProp)

                setSelectedOptions(newSelectedOptions)

                dispatch({
                    type: PPL_ACTIONS.setPremiumOptions,
                    payload: {
                        subPropIndex: index,
                        subPropertyOptions: newSubProp,
                        selectedOptions: newSelectedOptions,
                        groupIndex,
                    },
                })
            }

            setIsAddActive(false)
            dispatch?.({ type: PPL_ACTIONS.setOptionActive, payload: '' })

            return false
        },
        [
            setIsAddActive,
            setSelectedOptions,
            subPropertyOptions,
            dispatch,
            index,
            selectedOptions,
        ]
    )

    const handleAddPremPlusProperty = useCallback(() => {
        dispatch?.({
            type: PPL_ACTIONS.setOptionActive,
            payload: subProperty.optionGroups[groupIndex].groupId,
        })
    }, [dispatch, subProperty])

    const processPrefixPostfix = useCallback(
        (e: any, callback: SetStateAction<any>) => {
            // explicitly typed e as any since this is contenteditable
            if (!isAffixActive) {
                setIsAffixActive?.(true)
            }

            if (e.code === 'Enter' || e.key === 'Enter') {
                e.target.blur()
            } else if (
                e.target.innerText.length >= AFFIX_LIMIT &&
                e.key.length === 1 &&
                !e.ctrlKey &&
                !e.altKey
            ) {
                e.preventDefault()
                return
            } else {
                callback(
                    stripDoubleSpaces(e.target.innerText).slice(0, AFFIX_LIMIT)
                )
            }
        },
        [AFFIX_LIMIT, isAffixActive, setIsAffixActive, stripDoubleSpaces]
    )

    const handlePrefixChange = useCallback(
        (e) => {
            processPrefixPostfix(e, setGroupPrefix)
        },
        [setGroupPrefix, processPrefixPostfix]
    )

    const handlePostfixChange = useCallback(
        (e) => {
            processPrefixPostfix(e, setGroupPostfix)
        },
        [setGroupPostfix, processPrefixPostfix]
    )

    const noop = useCallback(() => {}, [])

    const handleGroupPrefixBlur = useCallback(
        (e) => {
            if (
                e.target.innerText !==
                subProperty.optionGroups[groupIndex].prefix
            ) {
                dispatch?.({
                    type: PPL_ACTIONS.setGroupPrefix,
                    payload: {
                        subPropIndex: index,
                        optionGroupIndex: groupIndex,
                        prefixText: e.target.innerText,
                    },
                })
            }
            setIsAffixActive?.(false)
        },
        [
            dispatch,
            index,
            groupIndex,
            groupPrefix,
            subProperty,
            setIsAffixActive,
        ]
    )

    const handleGroupPostfixBlur = useCallback(
        (e) => {
            if (
                e.target.innerText !==
                subProperty.optionGroups[groupIndex].postfix
            ) {
                dispatch?.({
                    type: PPL_ACTIONS.setGroupPostfix,
                    payload: {
                        subPropIndex: index,
                        optionGroupIndex: groupIndex,
                        postfixText: e.target.innerText,
                    },
                })
            }
            setIsAffixActive?.(false)
        },
        [
            dispatch,
            index,
            groupIndex,
            groupPostfix,
            subProperty,
            setIsAffixActive,
        ]
    )

    const handleSplitOptionsGroup = useCallback((text, optionIndex) => {
        dispatch({
            type: PPL_ACTIONS.setSubProperty,
            payload: {
                subProperty: {
                    ...subProperty,
                    optionGroups: [
                        ...(subProperty.optionGroups.slice(0, groupIndex) ??
                            null),
                        {
                            ...subProperty.optionGroups[groupIndex],
                            options: subProperty.optionGroups[
                                groupIndex
                            ].options?.slice(0, optionIndex),
                            postfix: '',
                        },
                        {
                            ...generateNewOptionGroups(),
                            prefix: text,
                            options: subProperty.optionGroups[
                                groupIndex
                            ].options?.slice(
                                optionIndex,
                                subPropertyOptions.length
                            ),
                            postfix:
                                subProperty.optionGroups[groupIndex].postfix,
                        },
                        ...(subProperty.optionGroups.slice(
                            groupIndex + 1 ?? 0,
                            subProperty.optionGroups.length
                        ) ?? null),
                    ],
                },
                subPropertyIndex: index,
            },
        })
    }, [])

    const onOptionBlur = useCallback(
        (id: string, value: string) => {
            setSubPropertyOptions((subPropArray) => {
                const optionIndex = subPropArray.findIndex(
                    (option) => option.optionId === id
                )
                const update = insertToIndex(
                    subPropArray,
                    optionIndex,
                    {
                        ...subPropArray[optionIndex],
                        value: stripDoubleSpaces(value),
                    },
                    true
                )

                dispatch({
                    type: PPL_ACTIONS.setPremiumOptions,
                    payload: {
                        subPropIndex: index,
                        subPropertyOptions: update,
                        selectedOptions,
                        groupIndex,
                    },
                })

                return update
            })
        },
        [index, selectedOptions, groupIndex, stripDoubleSpaces]
    )

    const onUOMBlur = useCallback(
        (property) => {
            setSubPropertyOptions((subPropArray) => {
                const optionIndex = subPropArray.findIndex(
                    (option) => option.optionId === property.optionId
                )
                const update = insertToIndex(
                    subPropArray,
                    optionIndex,
                    property,
                    true
                )

                dispatch({
                    type: PPL_ACTIONS.setPremiumOptions,
                    payload: {
                        subPropIndex: index,
                        subPropertyOptions: update,
                        selectedOptions,
                        groupIndex,
                    },
                })

                return update
            })
        },
        [index, selectedOptions, groupIndex]
    )

    useEffect(() => {
        if (
            !!state.optionActive &&
            subProperty.optionGroups[groupIndex].groupId ===
                state.optionActive &&
            !isAddActive
        ) {
            setIsAddActive(true)
        }
    }, [
        state.optionActive,
        setIsAddActive,
        isAddActive,
        subProperty.propertyId,
    ])

    // Focus on the first prefix if the text toggle is enabled
    useEffect(() => {
        if (
            !!isPrefixEditable &&
            editablePrefix.current &&
            groupIndex === 0 &&
            !subProperty.optionGroups[groupIndex].prefix
        ) {
            editablePrefix.current.focus()
        }
    }, [isPrefixEditable, subProperty.optionGroups[groupIndex].prefix])

    return (
        <Typography component="span" className={classes.optionGroupContainer}>
            <Typography
                suppressContentEditableWarning={true}
                component="span"
                contentEditable={!!isPrefixEditable}
                id={`${subProperty.propertyId}_${groupIndex}_prefix`}
                tabIndex={!!isPrefixEditable ? 0 : 1}
                className={clsx(
                    {
                        [classes.editable]: !!isPrefixEditable,
                    },
                    classes.prefixPostFix
                )}
                onKeyDown={handlePrefixChange}
                onBlur={handleGroupPrefixBlur}
                ref={editablePrefix}
            >
                {subProperty.optionGroups[groupIndex].prefix ?? ''}
            </Typography>

            <DndProperties
                isAddActive={isAddActive}
                dispatch={dispatch}
                setIsAddActive={setIsAddActive}
                propertyName={''}
                subPropertyName={subProperty.subPropertyName ?? ''}
                premiumOptions={subPropertyOptions}
                determineSelected={determineSelected}
                onDragEnd={handleDragEnd}
                onRemoveClick={handleRemoveClick}
                onSelectOption={handleSetSelected}
                handleInsertPremiumOption={insertPremiumOption}
                handlePrefixChange={handleSplitOptionsGroup}
                onOptionBlur={onOptionBlur}
                onUOMBlur={onUOMBlur}
                prefixEditable={isPrefixEditable}
            />

            <Typography
                suppressContentEditableWarning={true}
                component="span"
                contentEditable={!!isPrefixEditable && !!hasSubpropertyOptions}
                id={`${subProperty.propertyId}_${groupIndex}_postfix`}
                tabIndex={!!isPrefixEditable ? 0 : 1}
                className={clsx({
                    [classes.prefixPostFix]: !!subProperty.optionGroups[
                        groupIndex
                    ].postfix,
                    [classes.editable]: !!isPrefixEditable,
                })}
                onKeyDown={handlePostfixChange}
                onBlur={handleGroupPostfixBlur}
            >
                {subProperty.optionGroups[groupIndex].postfix ?? ' '}
            </Typography>

            <IconButton
                className={classes.addPremiumPlusProperty}
                onClick={isAddActive ? noop : handleAddPremPlusProperty}
                data-testid="addPremium"
            >
                <AddOutlined className={classes.addIcon} />
            </IconButton>

            {isRemoveLoading && (
                <Backdrop className={classes.backdrop} open={isRemoveLoading}>
                    <CircularProgress color="inherit" />
                </Backdrop>
            )}
        </Typography>
    )
}

export default NonMSEditableOptionGroup
